import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { isSameAuthSession } from '@app/authentification/user-identity';
import { BehaviorSubject, Observable, of, catchError, distinctUntilChanged, shareReplay, switchMap, take, throwError } from 'rxjs';

export class DataLoaderAuthentified<T> {
  private observable: Observable<T>;

  private cache: BehaviorSubject<T> = new BehaviorSubject(null);

  private accessing: Observable<T> = null;

  private options: any = null;

  constructor(
    private authentification: Observable<UserAuthentified>,
    private accessor: (
      authentification: UserAuthentified,
      val?: any,
    ) => Observable<T>,
    private defaultValue?: T,
    private log?: boolean,
  ) {
    this.observable = this.authentification.pipe(
      distinctUntilChanged(isSameAuthSession),
      switchMap(authentification => {
        if (authentification) {
          return this.cache.asObservable().pipe(
            switchMap(value => {
              // si on a une valeur en cache on la retourne
              if (value) {
                if (this.log) console.debug('from cache', value);
                return of(value);
              }

              // si ce n'est pas fait on lance le chargement
              if (!this.accessing) {
                this.accessing = accessor(authentification, this.options).pipe(
                  shareReplay(1),
                  switchMap((data: T) => {
                    this.cache.next(data);
                    return of(data);
                  }),
                  // Un appel en échec ne doit pas rester en cache : le
                  // shareReplay mémorise aussi les erreurs, et l'observable
                  // source est déjà figé ici. Sans cette remise à zéro, un aléa
                  // réseau isolé serait rejoué jusqu'à la reconnexion et la
                  // donnée ne serait jamais rechargée de la session.
                  // On ne touche pas au cache : le vider ici relancerait
                  // aussitôt un appel, dont le résultat écraserait celui de la
                  // lecture en cours.
                  catchError(error => {
                    this.accessing = null;
                    return throwError(() => error);
                  }),
                );
                if (this.log) console.debug('make the call', this.accessing);
              }

              // on renvoie le chargment en court
              if (this.log)
                console.debug('return the call observer', this.accessing);
              return this.accessing;
            }),
          );
        } else {
          return of(defaultValue || null);
        }
      }),
    );
  }

  /**
   * Flux VIF : ne complète jamais, ré-émet après reset().
   * Pour les lectures one-shot, utiliser current$().
   */
  get(options?: any): Observable<T> {
    this.options = options;
    return this.observable;
  }

  /**
   * Lecture one-shot : valeur courante (fetch si nécessaire), puis complète.
   * Pour les resolvers, snapshots et lectures internes aux chaînes d'écriture.
   *
   * Pas de filter sur null : la branche non authentifiée émet légitimement
   * null, et un resolver qui ne complète jamais gèle la navigation pour de
   * bon (le Router applique first(), sans timeout).
   */
  current$(options?: any): Observable<T> {
    return this.get(options).pipe(take(1));
  }

  reset(): Observable<T> {
    this.accessing = null;
    this.cache.next(null);
    return this.get();
  }

  setCache(entity: T): void {
    this.cache.next(entity);
  }
}
